// Minimal formatting helpers needed to render Summary tables/charts.
// NOTE: This partially fulfils T12; the full helper set can expand later.
// Components/charts should call these instead of formatting inline.

export const PLACEHOLDER = '--';

/**
 * Normalizes a backend/mock update timestamp to the widget's date-only header
 * format. The parser deliberately reads calendar parts instead of constructing
 * a local Date, so an API timezone cannot shift the displayed calendar day.
 */
export function formatUpdateDate(value: string | null | undefined): string {
  if (typeof value !== 'string') return PLACEHOLDER;

  const match = value.trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (!match) return PLACEHOLDER;

  const [, year, rawMonth, rawDay] = match;
  const month = Number(rawMonth);
  const day = Number(rawDay);
  const date = new Date(Date.UTC(Number(year), month - 1, day));
  if (
    month < 1 ||
    month > 12 ||
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return PLACEHOLDER;
  }

  return `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
}

/** Format a number with locale thousands separators. */
export function formatNumber(
  value: number | null | undefined,
  fractionDigits = 0
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return PLACEHOLDER;
  }
  return value.toLocaleString('ja-JP', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/** Format a percentage value (number already expressed as percent, e.g. 35.5). */
export function formatPercent(
  value: number | null | undefined,
  fractionDigits = 1
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return PLACEHOLDER;
  }
  return `${formatNumber(value, fractionDigits)}%`;
}

/** Format a signed value with explicit + sign for positives. */
export function formatSigned(
  value: number | null | undefined,
  fractionDigits = 2
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return PLACEHOLDER;
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumber(value, fractionDigits)}`;
}

/** Append a unit label to a formatted number (e.g. "34.24倍"). */
export function formatWithUnit(
  value: number | null | undefined,
  unit: string,
  fractionDigits = 2
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return PLACEHOLDER;
  }
  return `${formatNumber(value, fractionDigits)}${unit}`;
}
